/**
 * Group requirements and matching goals: effective limits, feasibility,
 * goal/requirement conflicts and the plain-language lines shown in
 * "What this means". Ported from the prototype.
 */
import {
  groupsRange,
  listLabel,
  rangeLabel,
  splitsIntoGroups,
  type ValueCount,
} from './derive';
import type {
  AvoidCombination,
  Column,
  ColumnRule,
  MatchingConfig,
  MatchingGoal,
  SpreadGoal,
} from './types';

/** Buddy mix allows 2–6 people of each type. */
export const BUDDY_MIN = 2;
export const BUDDY_MAX = 6;
/** Columns with more values than this show a "consider grouping" note when numeric, and a "Show all" toggle. */
export const SHOW_VALUES = 6;

export const CLAMP_MESSAGE = "The fewest can't be more than the most.";

export function lowerLabel(
  column: Column | undefined,
  fallback = 'value'
): string {
  return column ? column.label.toLowerCase() : fallback;
}

export function pluralLabel(label: string): string {
  return /s$|\)$/.test(label) ? label : `${label}s`;
}

// ---------------------------------------------------------------------------
// Goal construction
// ---------------------------------------------------------------------------

export type SpreadPreset = 'types' | 'buddy' | 'limits';

export function newGoal(
  column: Column,
  how: MatchingGoal['how'],
  valueCount: number,
  presets: SpreadPreset[] = []
): MatchingGoal {
  if (how === 'keep_together') return { column: column.key, how, saved: false };
  return {
    column: column.key,
    how: 'spread',
    saved: false,
    types: { min: Math.min(2, valueCount), max: null },
    buddy: { enabled: presets.includes('buddy'), minPerType: BUDDY_MIN },
    perValue: {},
    avoid: [],
  };
}

/** Switch a goal between "Same in each group" and "Spread out", keeping its settings where it can. */
export function switchHow(
  goal: MatchingGoal,
  how: MatchingGoal['how'],
  valueCount: number
): MatchingGoal {
  if (goal.how === how) return goal;
  if (how === 'keep_together')
    return { column: goal.column, how, saved: goal.saved };
  return {
    column: goal.column,
    how: 'spread',
    saved: goal.saved,
    types: { min: Math.min(2, valueCount), max: null },
    buddy: { enabled: false, minPerType: BUDDY_MIN },
    perValue: {},
    avoid: [],
  };
}

// ---------------------------------------------------------------------------
// Effective limits for a spread goal
// ---------------------------------------------------------------------------

/** Lowest per-value minimum: buddy mix forces at least `minPerType`. */
export function buddyFloor(goal: SpreadGoal): number {
  return goal.buddy.enabled ? goal.buddy.minPerType : 0;
}

export function typesMax(goal: SpreadGoal, valueCount: number): number {
  return goal.types.max === null
    ? valueCount
    : Math.min(goal.types.max, valueCount);
}

export function typesMin(goal: SpreadGoal, valueCount: number): number {
  return Math.min(Math.max(goal.types.min, 1), typesMax(goal, valueCount));
}

export function typesConstrained(
  goal: SpreadGoal,
  valueCount: number
): boolean {
  return (
    typesMin(goal, valueCount) > 1 || typesMax(goal, valueCount) < valueCount
  );
}

/**
 * Per-value limits apply to a group only when the value is in it: "a group
 * with any Women has `min` to `max` Women". So `min` is never below 1, and
 * it only forces the value into every group when `every` is set.
 */
export interface EffectiveLimit {
  min: number;
  /** `Infinity` means no limit. */
  max: number;
  every: boolean;
  /** The lowest `min` can go: 1, or the buddy-mix size when buddy mix is on. */
  floor: number;
}

export function valueLimit(goal: SpreadGoal, value: string): EffectiveLimit {
  const r = goal.perValue[value] ?? {};
  const every = !!r.inEveryGroup;
  const floor = Math.max(buddyFloor(goal), 1);
  const min = Math.max(r.min ?? floor, floor);
  return { min, max: Math.max(r.max ?? Infinity, min), every, floor };
}

export function isRanged(limit: EffectiveLimit): boolean {
  return limit.min > limit.floor || limit.max < Infinity;
}

export function limitText(limit: EffectiveLimit): string {
  if (limit.min > limit.floor && limit.max < Infinity) {
    return limit.min === limit.max
      ? `exactly ${limit.min}`
      : `${limit.min} to ${limit.max}`;
  }
  return limit.min > limit.floor
    ? `at least ${limit.min}`
    : `at most ${limit.max}`;
}

export function avoidText(c: AvoidCombination): string {
  const list = listLabel(c.values);
  return c.match === 'any'
    ? `Never a group that has ${list} together.`
    : `Never a group made up of exactly ${list}.`;
}

// ---------------------------------------------------------------------------
// Plain-language lines
// ---------------------------------------------------------------------------

export function goalLines(
  goal: MatchingGoal,
  column: Column | undefined,
  values: ValueCount[]
): string[] {
  const label = lowerLabel(column);
  if (goal.how === 'keep_together')
    return [`Everyone in a group has the same ${label}.`];
  const lines: string[] = [];
  const n = values.length;
  if (typesConstrained(goal, n)) {
    lines.push(
      `Every group has ${rangeLabel(typesMin(goal, n), typesMax(goal, n))} different types of ${label}.`
    );
  }
  if (goal.buddy.enabled) {
    lines.push(
      `Buddy mix: everyone has at least one other person of their same type. Every type in a group has at least ${goal.buddy.minPerType} people.`
    );
  }
  for (const { value } of values) {
    const r = valueLimit(goal, value);
    if (r.every && isRanged(r))
      lines.push(`${value}: in every group, ${limitText(r)} per group.`);
    else if (r.every) lines.push(`${value}: in every group.`);
    else if (isRanged(r)) lines.push(`${value}: ${limitText(r)} per group.`);
  }
  goal.avoid.forEach(c => lines.push(avoidText(c)));
  if (lines.length === 0)
    lines.push('No limits yet. Groups are mixed without extra requirements.');
  return lines;
}

export function ruleTitle(
  rule: ColumnRule,
  column: Column | undefined
): string {
  const l = lowerLabel(column);
  return rule.type === 'keep_together'
    ? `Enforce keeping ${l} together`
    : `Enforce no ${l} overlap`;
}

export function ruleDescription(
  rule: ColumnRule,
  column: Column | undefined
): string {
  const l = lowerLabel(column);
  return rule.type === 'keep_together'
    ? `Everyone in a group has the same ${l}.`
    : `No two people with the same ${l} are in the same group.`;
}

// ---------------------------------------------------------------------------
// Feasibility
// ---------------------------------------------------------------------------

export interface Feasibility {
  ok: boolean;
  message: string;
}

/** A goal that contradicts an enforced requirement on the same column. */
export function goalRuleConflict(
  goal: MatchingGoal,
  config: MatchingConfig,
  column: Column | undefined,
  values: ValueCount[]
): string {
  const rule = config.columnRules.find(
    r => r.column === goal.column && r.enabled
  );
  if (!rule) return '';
  const label = lowerLabel(column);
  const head = `Conflicts with your group requirement "${ruleTitle(rule, column)}". `;
  if (rule.type === 'no_overlap') {
    if (goal.how === 'keep_together') {
      return `${head}The requirement means no two people in a group share a ${label}, but this matching goal needs everyone in a group to share one.`;
    }
    if (goal.buddy.enabled) {
      return `${head}The requirement means nobody in a group has someone with the same ${label}, but buddy mix needs everyone to have at least one.`;
    }
    const multi = values.find(v => valueLimit(goal, v.value).min > 1);
    if (multi) {
      return `${head}The requirement allows only one person of each ${label} per group, but this matching goal needs at least ${valueLimit(goal, multi.value).min} ${multi.value}.`;
    }
  } else if (goal.how === 'spread') {
    if (typesMin(goal, values.length) > 1) {
      return `${head}The requirement keeps every group to a single ${label}, but this matching goal needs at least ${typesMin(goal, values.length)} different types per group.`;
    }
    if (values.filter(v => valueLimit(goal, v.value).every).length > 1) {
      return `${head}The requirement keeps every group to a single ${label}, but this matching goal needs more than one type in every group.`;
    }
  }
  return '';
}

// ---------------------------------------------------------------------------
// Combinations to avoid
//
// matching-server judges a group by its mix: the set of types in it. It
// rejects a mix that includes every value of an "All of these together" combination,
// or is exactly the values of an "Exact combination". Values marked "in
// every group" are in every mix, so some combinations can't be avoided.
// ---------------------------------------------------------------------------

/** Beyond this many optional types, there are too many mixes to check them all. */
const MAX_CHECKED_OPTIONAL_TYPES = 12;

function sameValues(a: Iterable<string>, b: string[]): boolean {
  const set = new Set(a);
  return set.size === new Set(b).size && b.every(v => set.has(v));
}

function avoids(mix: Set<string>, c: AvoidCombination): boolean {
  return c.match === 'any'
    ? !c.values.every(v => mix.has(v))
    : !sameValues(mix, c.values);
}

/** The only mix a group can have, or `null` when there is a choice. */
function forcedMix(goal: SpreadGoal, values: ValueCount[]): string[] | null {
  const required = values.filter(v => valueLimit(goal, v.value).every);
  const fewest = Math.max(required.length, typesMin(goal, values.length));
  const most = typesMax(goal, values.length);
  if (most === required.length) return required.map(v => v.value);
  if (fewest === values.length) return values.map(v => v.value);
  return null;
}

/** Why every group would have this combination, or `''`. */
export function combinationConflict(
  goal: SpreadGoal,
  values: ValueCount[],
  c: AvoidCombination
): string {
  const list = listLabel(c.values);
  if (
    c.match === 'any' &&
    c.values.every(v => values.some(x => x.value === v)) &&
    c.values.every(v => valueLimit(goal, v).every)
  ) {
    return `${list} must all be in every group, but you're avoiding groups that have ${list} together.`;
  }
  const forced = forcedMix(goal, values);
  if (c.match === 'exact' && forced && sameValues(forced, c.values)) {
    return `Every group has to be made up of exactly ${list}, but that's a combination you're avoiding.`;
  }
  return '';
}

/** Is there a mix of types that meets the type limits and avoids every combination? */
function someMixAvoidsAll(goal: SpreadGoal, values: ValueCount[]): boolean {
  if (goal.avoid.length === 0) return true;
  const required = values
    .filter(v => valueLimit(goal, v.value).every)
    .map(v => v.value);
  const optional = values.map(v => v.value).filter(v => !required.includes(v));
  if (optional.length > MAX_CHECKED_OPTIONAL_TYPES) return true;
  const fewest = typesMin(goal, values.length);
  const most = typesMax(goal, values.length);
  for (let bits = 0; bits < 1 << optional.length; bits++) {
    const mix = new Set(required);
    optional.forEach((v, i) => {
      if (bits & (1 << i)) mix.add(v);
    });
    if (mix.size < fewest || mix.size > most) continue;
    if (goal.avoid.every(c => avoids(mix, c))) return true;
  }
  return false;
}

/** Why the combinations to avoid rule out every group, or `''`. */
export function avoidConflict(goal: SpreadGoal, values: ValueCount[]): string {
  for (const c of goal.avoid) {
    const conflict = combinationConflict(goal, values, c);
    if (conflict) return `Not possible. ${conflict}`;
  }
  return someMixAvoidsAll(goal, values)
    ? ''
    : "Not possible. Every mix of types a group could have includes a combination you're avoiding.";
}

export function goalFeasibility(
  goal: MatchingGoal,
  config: MatchingConfig,
  column: Column | undefined,
  values: ValueCount[],
  participantCount: number
): Feasibility {
  const size = config.groupSize;
  const groups = groupsRange(participantCount, size);
  const sizeText = rangeLabel(size.min, size.max);

  const conflict = goalRuleConflict(goal, config, column, values);
  if (conflict) return { ok: false, message: conflict };

  if (goal.how === 'keep_together') {
    const bad = values.filter(v => !splitsIntoGroups(v.count, size));
    if (bad.length > 0) {
      const more =
        bad.length > 1
          ? ` ${bad.length - 1} more values have the same problem.`
          : '';
      return {
        ok: false,
        message: `${bad[0].value} has ${bad[0].count} people. That can't be split into whole groups of ${sizeText}.${more}`,
      };
    }
    return {
      ok: true,
      message: `Every value can be split into whole groups of ${sizeText}.`,
    };
  }

  const n = values.length;
  const limits = values.map(v => ({ ...v, limit: valueLimit(goal, v.value) }));
  const inEvery = limits.filter(v => v.limit.every);
  if (goal.types.min > n) {
    return {
      ok: false,
      message: `Not possible. There are only ${n} types, but groups need at least ${goal.types.min}.`,
    };
  }
  if (inEvery.length > typesMax(goal, n)) {
    return {
      ok: false,
      message: `Not possible. ${inEvery.length} types must be in every group, but a group can have at most ${typesMax(goal, n)} types.`,
    };
  }
  const avoid = avoidConflict(goal, values);
  if (avoid) return { ok: false, message: avoid };
  // Only values marked "in every group" take up room in every group. Any
  // other value's Fewest applies only to the groups it ends up in.
  const everyMin = inEvery.reduce((sum, v) => sum + v.limit.min, 0);
  if (everyMin > size.max) {
    return {
      ok: false,
      message: `Not possible. The fewest of each type that must be in every group adds up to ${everyMin}, but the largest group is ${size.max}.`,
    };
  }
  for (const { value, count, limit: r } of limits) {
    if (count === 0) continue;
    if (r.every && r.min * groups.lo > count) {
      return {
        ok: false,
        message: `Not possible. ${value} must be in every group with at least ${r.min}, which needs ${r.min * groups.lo} people across ${groups.lo} groups. There are ${count}.`,
      };
    }
    if (r.max < Infinity && r.max * groups.hi < count) {
      return {
        ok: false,
        message: `Not possible. At most ${r.max} ${value} per group cannot place all ${count} across ${groups.hi} groups.`,
      };
    }
    if (goal.buddy.enabled && count < goal.buddy.minPerType) {
      return {
        ok: false,
        message: `Not possible. Only ${count} ${value}, fewer than the ${goal.buddy.minPerType} a buddy mix needs.`,
      };
    }
    // Every group that has this value needs min to max of it, so the people
    // must split into some number of groups k with k·min ≤ count ≤ k·max.
    const fewestGroups = r.max === Infinity ? 1 : Math.ceil(count / r.max);
    const mostGroups = Math.floor(count / r.min);
    if (fewestGroups > mostGroups) {
      return {
        ok: false,
        message:
          count < r.min
            ? `Not possible. Only ${count} ${value}, fewer than the ${r.min} a group with any ${value} needs.`
            : `Not possible. The ${count} ${value} can't be split into groups that each have ${limitText(r)} ${value}.`,
      };
    }
  }
  return { ok: true, message: `Looks possible across ${groups.label} groups.` };
}

/** A requirement that can't be met with this cohort's numbers. */
export function ruleIssue(
  rule: ColumnRule,
  config: MatchingConfig,
  column: Column | undefined,
  values: ValueCount[],
  participantCount: number
): string {
  if (!rule.enabled || values.length === 0) return '';
  const size = config.groupSize;
  const label = lowerLabel(column);
  if (rule.type === 'no_overlap') {
    if (values.length < size.min) {
      return `Only ${values.length} different ${label} values, so groups of ${size.min} or more can't avoid overlap.`;
    }
    const groups = groupsRange(participantCount, size);
    const largest = values.reduce((a, b) => (b.count > a.count ? b : a));
    if (largest.count > groups.hi) {
      return `${largest.value} has ${largest.count} people, but there are at most ${groups.hi} groups to spread them across.`;
    }
    return '';
  }
  const bad = values.find(v => !splitsIntoGroups(v.count, size));
  return bad
    ? `${bad.value} has ${bad.count} people, which can't be split into whole groups of ${rangeLabel(size.min, size.max)}.`
    : '';
}

/** A requirement whose column also has a conflicting matching goal. */
export function ruleConflict(
  rule: ColumnRule,
  config: MatchingConfig,
  column: Column | undefined,
  values: ValueCount[]
): string {
  if (!rule.enabled) return '';
  const goal = config.goals.find(g => g.column === rule.column);
  if (!goal || !goalRuleConflict(goal, config, column, values)) return '';
  return `Conflicts with your ${column?.label ?? rule.column} matching goal. See the matching goal for details.`;
}
